
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type BuyCourseForm,
  type PaymentMethod,
  VitrinService,
} from "@/services/vitrin-service";
import { useProfileStore } from "@/store/profile-store";
import { isEitaaMiniApp } from "@/utils/utilities";
import { isProfileComplete } from "@/utils/profile";
import { trackCheckoutStarted } from "@/features/analytics";

interface UseCoursePurchaseOptions {
  miniAppUuid: string;
  courseId: number;
  shouldBuy: boolean;
  requiresCompleteProfile?: boolean;
  onOpenCourse: () => void;
  onCloseDrawer: () => void;
  onNeedProfileCompletion?: () => void;
}

const buildDirectCallbackUrl = (
  successUrl: string,
  fields: NonNullable<BuyCourseForm["inputs"] | BuyCourseForm["fields"]>,
  miniAppUuid: string,
  courseId: number,
) => {
  const url = new URL(successUrl, window.location.origin);

  if (url.hostname === "localhost") {
    url.protocol = window.location.protocol;
    url.host = window.location.host;
  }

  const callbackFields: Record<string, string> = {
    price: "price",
    title: "courseName",
  };

  Object.entries(callbackFields).forEach(([fieldKey, queryKey]) => {
    const value = fields[fieldKey];
    if (value !== undefined && value !== null && !url.searchParams.has(queryKey)) {
      url.searchParams.set(queryKey, String(value));
    }
  });

  if (!url.searchParams.has("miniAppUuid")) {
    url.searchParams.set("miniAppUuid", miniAppUuid);
  }

  if (!url.searchParams.has("courseId")) {
    url.searchParams.set("courseId", String(courseId));
  }

  return url.toString();
};

export const submitGatewayForm = (form: BuyCourseForm, miniAppUuid: string, courseId: number) => {
  if (typeof document === "undefined") return false;

  const fields = form.inputs ?? form.fields ?? {};
  const successUrl = typeof fields.successUrl === "string" ? fields.successUrl : null;
  const normalizedFields = { ...fields };

  if (successUrl) {
    normalizedFields.successUrl = buildDirectCallbackUrl(successUrl, fields, miniAppUuid, courseId);
  }

  if (!form.action) {
    if (!successUrl) return false;
    window.location.assign(String(normalizedFields.successUrl));
    return true;
  }

  const htmlForm = document.createElement("form");
  htmlForm.method = form.method || "POST";
  htmlForm.action = form.action;
  htmlForm.style.display = "none";

  Object.entries(normalizedFields).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = key;
    input.value = String(value);
    htmlForm.appendChild(input);
  });

  document.body.appendChild(htmlForm);
  htmlForm.submit();
  return true;
};

export const useCoursePurchase = ({
  miniAppUuid,
  courseId,
  shouldBuy,
  requiresCompleteProfile,
  onOpenCourse,
  onCloseDrawer,
  onNeedProfileCompletion,
}: UseCoursePurchaseOptions) => {
  const [isBuying, setIsBuying] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const sendMobile = useProfileStore((s) => s.sendMobile);
  const queryClient = useQueryClient();

  const handleBuyCourse = useCallback(async (
    discountId?: number,
    paymentMethod?: PaymentMethod,
  ) => {
    setErrorText(null);

    if (!shouldBuy) {
      onCloseDrawer();
      onOpenCourse();
      return;
    }

    // In Eitaa miniapp, the phone must be linked before purchase. Eitaa's
    // requestContact opens the in-app share-contact prompt; the backend
    // attaches the phone to the user on `auth/jwt/link-phone`.
    const { profile } = useProfileStore.getState();
    if (isEitaaMiniApp() && !profile?.phone) {
      const initData = window.Eitaa?.WebApp?.initData ?? "";
      setIsBuying(true);
      try {
        const result = await sendMobile(initData);
        if (result === "CANCELED") {
          setErrorText("برای ادامه خرید، اشتراک‌گذاری شماره موبایل لازم است");
          return;
        }
        if (result === "ERROR") {
          setErrorText("ثبت شماره موبایل با خطا مواجه شد");
          return;
        }
      } finally {
        setIsBuying(false);
      }
    }

    // Course-level requirement: profile must be complete before buy. Hand
    // off to the host so it can render the completion drawer; the host will
    // call handleBuyCourse again after a successful save. Callers that don't
    // wire `onNeedProfileCompletion` opt out of the gate and let the backend
    // reject if needed.
    if (requiresCompleteProfile && onNeedProfileCompletion) {
      const { profile: latestProfile } = useProfileStore.getState();
      if (!isProfileComplete(latestProfile)) {
        onCloseDrawer();
        onNeedProfileCompletion();
        return;
      }
    }

    setIsBuying(true);
    try {
      trackCheckoutStarted({
        course_id: courseId,
        mini_app_uuid: miniAppUuid,
        coupon_code: discountId ? String(discountId) : undefined,
      });

      const response = await VitrinService.buyCourse(miniAppUuid, courseId, {
        source: 1,
        ...(discountId ? { discount_id: discountId } : {}),
        ...(paymentMethod ? { payment_method: paymentMethod } : {}),
      });

      if (!response.success || !response.data) {
        setErrorText(response.message || "خطا در ثبت خرید دوره");
        return;
      }

      if (response.data.enrolled) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["vitrin-course"] }),
          queryClient.invalidateQueries({ queryKey: ["vitrin-course-lessons"] }),
          queryClient.invalidateQueries({ queryKey: ["my-courses"] }),
        ]);
        onCloseDrawer();
        onOpenCourse();
        return;
      }

      if (response.data.form) {
        const didSubmit = submitGatewayForm(response.data.form, miniAppUuid, courseId);
        if (didSubmit) return;
      }

      setErrorText("امکان هدایت به درگاه پرداخت وجود ندارد");
    } catch {
      setErrorText("درخواست خرید با خطا مواجه شد");
    } finally {
      setIsBuying(false);
    }
  }, [
    courseId,
    miniAppUuid,
    onCloseDrawer,
    onNeedProfileCompletion,
    onOpenCourse,
    queryClient,
    requiresCompleteProfile,
    sendMobile,
    shouldBuy,
  ]);

  return {
    isBuying,
    errorText,
    handleBuyCourse,
  } as const;
};
